// Shared between Wizard.jsx, preset chooser, and step components so they
// all start from the same shape as server/internal/pricing/pricing.go Inputs.
//
// Per Carolina's "force input before calculating" feedback: every input
// that's currently a median (RQUCL=3, samples=10K, diversity=3, consent=GRU)
// starts as null on a "Start fresh" valuation. followUpYears is optional
// (null = no follow-up entered; 0 / 0.5 / N years are all valid when set).
// The Wizard
// gates /api/v1/calculate on isComplete() and the Save buttons on the
// same — null inputs never reach the server (which validates 1..5 /
// samples >= 1 / etc.). Presets and saved valuations arrive with these
// fields populated, so their flow is unchanged.
//
// Step 4 partnership fields (exclusivity, multiYear, dataEnrichment,
// supportLevel) keep their operational defaults — those are baseline
// deal terms, not medians, so a non-exclusive 1-year self-service
// contract is a meaningful zero-config answer rather than a guess.
export const defaultInputs = {
  rarity: null,
  quality: null,
  usefulness: null,
  compliance: null,
  linkageRichness: null,
  samples: null,
  dataTypes: [],
  followUpYears: null,
  populationDiversity: null,
  duoPermission: null,
  duoModifiers: [],
  exclusivity: false,
  multiYear: 1,
  dataEnrichment: false,
  supportLevel: 'self-service',
  diseaseCategory: 'none',
};

export const FACTOR_KEYS = ['rarity', 'quality', 'usefulness', 'compliance', 'linkageRichness'];

function isRated(v) {
  return typeof v === 'number' && Number.isFinite(v) && v >= 1 && v <= 5;
}

// True iff every RQUCL factor has been actively rated. Used by the
// FactorCard glow / Step 1 chrome.
export function hasAllFactors(inputs) {
  if (!inputs) return false;
  return FACTOR_KEYS.every((f) => isRated(inputs[f]));
}

// True iff every input the server requires has been actively set —
// the gate for /api/v1/calculate. Step 4 partnership fields keep
// their operational defaults and don't gate anything. dataTypes is
// NOT gated: the server accepts an empty list and dataTypeMultiplier
// returns 1.0 — a dataset with no listed modality is a real state.
export function isComplete(inputs) {
  if (!inputs) return false;
  if (!hasAllFactors(inputs)) return false;
  if (!Number.isFinite(inputs.samples) || inputs.samples < 1) return false;
  if (
    inputs.followUpYears != null
    && (!Number.isFinite(inputs.followUpYears) || inputs.followUpYears < 0)
  ) return false;
  if (!isRated(inputs.populationDiversity)) return false;
  if (typeof inputs.duoPermission !== 'string' || inputs.duoPermission === '') return false;
  return true;
}

// firstIncompleteFor(step, inputs) returns the field name of the next
// thing that needs the user's attention on a given step, or null when
// the step is complete. Steps pass the result down so the matching
// input gets the amber glow ring.
export function firstIncompleteFor(step, inputs) {
  if (!inputs) return null;
  switch (step) {
    case 1: {
      for (const f of FACTOR_KEYS) {
        if (!isRated(inputs[f])) return f;
      }
      return null;
    }
    case 2:
      if (!Number.isFinite(inputs.samples) || inputs.samples < 1) return 'samples';
      if (!isRated(inputs.populationDiversity)) return 'populationDiversity';
      return null;
    case 3:
      if (typeof inputs.duoPermission !== 'string' || inputs.duoPermission === '') return 'duoPermission';
      return null;
    default:
      return null;
  }
}

// First wizard step (1–3) that still has required inputs missing.
export function firstIncompleteStep(inputs) {
  for (let step = 1; step <= 3; step += 1) {
    if (firstIncompleteFor(step, inputs) !== null) return step;
  }
  return null;
}

const FACTOR_FIELD_LABELS = {
  rarity: 'Rarity',
  quality: 'Quality',
  usefulness: 'Usefulness',
  compliance: 'Compliance',
  linkageRichness: 'Linkage richness',
};

// Field-level copy for the clickable Estimate / rail blocker card.
export function incompleteBlocker(inputs) {
  const step = firstIncompleteStep(inputs);
  if (!step) return null;
  const field = firstIncompleteFor(step, inputs);
  const stepLabel = WIZARD_STEP_LABELS[step - 1] || 'Wizard';

  switch (field) {
    case 'rarity':
    case 'quality':
    case 'usefulness':
    case 'compliance':
    case 'linkageRichness':
      return {
        step,
        stepLabel,
        field,
        title: `${FACTOR_FIELD_LABELS[field]} not rated`,
        detail: 'Open the factor card and choose a score.',
      };
    case 'samples':
      return {
        step,
        stepLabel,
        field,
        title: 'Cohort size missing',
        detail: 'Enter the number of patients in your dataset.',
      };
    case 'populationDiversity':
      return {
        step,
        stepLabel,
        field,
        title: 'Population diversity not set',
        detail: 'Move the diversity slider to describe your cohort.',
      };
    case 'duoPermission':
      return {
        step,
        stepLabel,
        field,
        title: 'Consent permission missing',
        detail: 'Select a DUO permission level for your dataset.',
      };
    default:
      return {
        step,
        stepLabel,
        field: field || null,
        title: 'Required inputs missing',
        detail: 'Finish the fields highlighted in amber on this step.',
      };
  }
}

export const WIZARD_STEP_LABELS = ['Quality', 'Specifications', 'Consent', 'Partnership', 'Estimate'];
